import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsCallJoined,
  setIsVideoMinimize,
} from "../../redux/slices/module.slice";
import { RootState } from "../../redux/store/store";

type CallState = "inactive" | "joining" | "active" | "ended";

interface JitsiContextType {
  isOpen: boolean;
  isMinimized: boolean;
  callState: CallState;
  callLink: string;
  roomName: string;
  displayName: string;
  appointmentUuid: string;
  occurrenceDate: string;
  openJitsi: (
    link: string,
    roomName: string,
    displayName: string,
    appointmentUuid?: string,
    occurrenceDate?: string,
  ) => void;
  closeJitsi: () => void;
  endCall: () => void;
  rejoinCall: () => void;
  setCallActive: () => void;
  minimizeJitsi: () => void;
  maximizeJitsi: () => void;
}

const JitsiContext = createContext<JitsiContextType>({
  isOpen: false,
  isMinimized: false,
  callState: "inactive",
  callLink: "",
  roomName: "",
  displayName: "",
  appointmentUuid: "",
  occurrenceDate: "",
  openJitsi: () => {
    throw new Error("openJitsi function must be used within a JitsiProvider");
  },
  closeJitsi: () => {
    throw new Error("closeJitsi function must be used within a JitsiProvider");
  },
  endCall: () => {
    throw new Error("endCall function must be used within a JitsiProvider");
  },
  rejoinCall: () => {
    throw new Error("rejoinCall function must be used within a JitsiProvider");
  },
  setCallActive: () => {
    throw new Error(
      "setCallActive function must be used within a JitsiProvider",
    );
  },
  minimizeJitsi: () => {
    throw new Error(
      "minimizeJitsi function must be used within a JitsiProvider",
    );
  },
  maximizeJitsi: () => {
    throw new Error(
      "maximizeJitsi function must be used within a JitsiProvider",
    );
  },
});

export const JitsiProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [callState, setCallState] = useState<CallState>("inactive");
  const [callLink, setCallLink] = useState("");
  const [roomName, setRoomName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [appointmentUuid, setAppointmentUuid] = useState("");
  const [occurrenceDate, setOccurrenceDateState] = useState("");
  const dispatch = useDispatch();
  const { isVideoMinimize } = useSelector((state: RootState) => state.module);

  useEffect(() => {
    if (isVideoMinimize) {
      maximizeJitsi();
      dispatch(setIsVideoMinimize(false));
    }
  }, [isVideoMinimize]);

  const cleanup = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    setCallState("inactive");
    setCallLink("");
    setRoomName("");
    setDisplayName("");
    setAppointmentUuid("");
    setOccurrenceDateState("");
    dispatch(setIsCallJoined(false));
    dispatch(setIsVideoMinimize(false));

    const jitsiElements = document.querySelectorAll('[id^="jitsi-"]');
    jitsiElements.forEach((element) => element.remove());
  }, []);

  const openJitsi = useCallback(
    (
      link: string,
      room: string,
      name: string,
      uuid: string = "",
      occurrence: string = "",
    ) => {
      cleanup();

      setCallLink(link);
      setRoomName(room);
      setDisplayName(name);
      setAppointmentUuid(uuid);
      setOccurrenceDateState(occurrence);
      setIsOpen(true);
      setIsMinimized(false);
      setCallState("joining");
    },
    [cleanup],
  );

  const closeJitsi = useCallback(() => {
    cleanup();
  }, [cleanup]);

  const endCall = useCallback(() => {
    setCallState("ended");
    dispatch(setIsCallJoined(false));
    setIsMinimized(true); // Show the ended call screen in minimized view
  }, []);

  const rejoinCall = useCallback(() => {
    setCallState("joining"); // Back to joining state
    setIsMinimized(false); // Keep it maximized
  }, []);

  const setCallActive = useCallback(() => {
    setCallState("active");
  }, []);

  const minimizeJitsi = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeJitsi = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Listen for navigation events to ensure proper cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanup();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <JitsiContext.Provider
      value={{
        isOpen,
        isMinimized,
        callState,
        callLink,
        roomName,
        displayName,
        appointmentUuid,
        occurrenceDate,
        openJitsi,
        closeJitsi,
        endCall,
        rejoinCall,
        setCallActive,
        minimizeJitsi,
        maximizeJitsi,
      }}
    >
      {children}
    </JitsiContext.Provider>
  );
};

export const useJitsi = () => useContext(JitsiContext);
